import { supabase } from '../lib/supabase';

/**
 * Storage service for endo report images (radiographs and clinical photos)
 */
export const reportImageStorageService = {
  /**
   * Upload image to endo-report-images bucket
   * @param {File} file - Image file to upload
   * @param {string} practitionerId - Practitioner UUID
   * @param {string} reportId - Report UUID
   * @returns {Promise<{path: string, url: string}>} File path and signed URL
   */
  async uploadImage(file, practitionerId, reportId) {
    try {
      // Generate unique filename with timestamp
      const timestamp = Date.now();
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${timestamp}-${file?.name?.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
      
      // Path format: practitioner_id/report_id/filename
      const filePath = `${practitionerId}/${reportId}/${fileName}`;

      // Upload to storage
      const { data, error } = await supabase?.storage
        ?.from('endo-report-images')
        ?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      // Generate signed URL (valid for 1 year for medical records)
      const { data: urlData, error: urlError } = await supabase?.storage
        ?.from('endo-report-images')
        ?.createSignedUrl(filePath, 31536000); // 1 year in seconds

      if (urlError) throw urlError;

      return {
        path: filePath,
        url: urlData?.signedUrl,
        fileName: file?.name,
        fileSize: file?.size,
        fileType: file?.type
      };
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  },

  /**
   * Get signed URL for existing image
   * @param {string} filePath - Storage file path
   * @returns {Promise<string>} Signed URL
   */
  async getSignedUrl(filePath) {
    try {
      const { data, error } = await supabase?.storage
        ?.from('endo-report-images')
        ?.createSignedUrl(filePath, 31536000); // 1 year

      if (error) throw error;

      return data?.signedUrl;
    } catch (error) {
      console.error('Error getting signed URL:', error);
      throw error;
    }
  },

  /**
   * Delete image from storage
   * @param {string} filePath - Storage file path
   */
  async deleteImage(filePath) {
    try {
      const { error } = await supabase?.storage
        ?.from('endo-report-images')
        ?.remove([filePath]);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting image:', error);
      throw error;
    }
  },

  /**
   * Delete all images for a report
   * @param {string} practitionerId - Practitioner UUID
   * @param {string} reportId - Report UUID
   */
  async deleteReportImages(practitionerId, reportId) {
    try {
      // List all files in report folder
      const { data: files, error: listError } = await supabase?.storage
        ?.from('endo-report-images')
        ?.list(`${practitionerId}/${reportId}`);

      if (listError) throw listError;

      if (files && files?.length > 0) {
        // Delete all files
        const filePaths = files?.map(file => `${practitionerId}/${reportId}/${file?.name}`);
        const { error: deleteError } = await supabase?.storage
          ?.from('endo-report-images')
          ?.remove(filePaths);

        if (deleteError) throw deleteError;
      }
    } catch (error) {
      console.error('Error deleting report images:', error);
      throw error;
    }
  },

  /**
   * Convert image file to base64 for PDF embedding
   * @param {string} signedUrl - Signed URL of image
   * @returns {Promise<string>} Base64 data URL
   */
  async imageToBase64(signedUrl) {
    try {
      const response = await fetch(signedUrl);
      const blob = await response?.blob();
      
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader?.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error converting image to base64:', error);
      throw error;
    }
  }
};
